import React, { CSSProperties, ReactNode } from 'react';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

const PaneContainer = styled.div`
    position: relative;
    margin-top: 1rem;
    border: 1.5px solid ${props => alpha(props.theme.palette.primary.main, 0.45)};
    border-radius: 8px;
    
    /* Increased top padding slightly so content doesn't collide with the header */
    padding: 0.5rem 0.5rem 0.5rem 0.5rem; 
`;

const HeaderWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    /* Centers the header exactly over the top border */
    transform: translateY(-50%); 
    display: flex;
    align-items: center;
    padding: 0 0.75rem;
    
    /* Prevents the invisible wrapper from blocking clicks on inputs inside the pane */
    pointer-events: none; 
`;

const HeaderSection = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    /* Creates the gap in the border */
    background-color: #ffffff; 
    padding: 0 0.5rem;
    
    /* Re-enables clicks for buttons inside the header */
    pointer-events: auto; 
`;

const RightSection = styled(HeaderSection)`
    /* Pushes the right section to the edge, even if the left section is missing */
    margin-left: auto; 
`;

const Title = styled(Typography)`
    color: ${props => props.theme.palette.primary.main}; /* Solid version of the border color */
    font-weight: 500;
    text-transform: uppercase; /* Optional: often looks good on captions */
`;

interface FormSectionPaneProps {
    title?: string | ReactNode;
    leftControls?: ReactNode;
    rightControls?: ReactNode;
    children?: ReactNode;
    className?: string; // Allows extending with styled()
    style?: CSSProperties;
}

export const FormSectionPane = ({
    title,
    leftControls,
    rightControls,
    children,
    className,
    style
}: FormSectionPaneProps) => {
    const hasLeft = !!title || !!leftControls;
    const hasRight = !!rightControls;
    const hasHeader = hasLeft || hasRight;

    return (
        <PaneContainer className={className} style={style}>
            {hasHeader && (
                <HeaderWrapper>
                    {hasLeft && (
                        <HeaderSection>
                            {title && (
                                <Title variant="caption">
                                    {title}
                                </Title>
                            )}
                            {leftControls}
                        </HeaderSection>
                    )}

                    {hasRight && (
                        <RightSection>
                            {rightControls}
                        </RightSection>
                    )}
                </HeaderWrapper>
            )}
            {children}
        </PaneContainer>
    );
};